/**
 * Component Registry for Weidner Apartment Homes Oasis Theme
 * 
 * Extends the base oasis theme registry with Weidner Apartment Homes Oasis specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-oasis/registry';
import { Footer, FutureResidentsClub, Header, ContactBlock, TourBlock, Hero, HeroSolidBG, AccessibilityStatement, GalleryStaggered, HomeAmenities, HomeNeighborhood, PropertyOverview, WhiteBlock, PetPolicy, Deals, PricingVerbiage, AmenitiesList } from './components';

/**
 * Weidner Apartment Homes Oasis specific component overrides.
 * These components have been customized for the Weidner Apartment Homes Oasis brand.
 *
 * - FutureResidentsClub: forked from the base oasis theme so the default
 *   section heading ("All the perks") and CTA ("APPLY NOW" →
 *   property_apply_now_url) match the canonical Weidner Future Residents
 *   Club page as shipped on via6living.com/future-residents-club.
 *
 * - Header: forked from the base oasis theme so the desktop utility row
 *   and mobile drawer match the canonical Weidner via6 header. Key
 *   structural deltas: the left-side social-icon sub-cluster is replaced
 *   by an address link; a new Qualifying Criteria link is inserted as
 *   the first item in the right-side cluster
 *   ([Qualifying Criteria | Residents | Apply Now]); and the same
 *   Qualifying Criteria affordance is added as a full-width pill in the
 *   mobile drawer above the Residents / Apply Now grid.
 *
 * - Footer: forked from the base oasis theme to surface the three
 *   contact/community affordances the canonical Weidner via6 footer
 *   (Repli-Multihub/vibesites-weidner-apartment-homes-via6, rendered
 *   at site-via-six.pages.dev) ships. The base already had the
 *   `footer.text_us_link` slot but rendered it as a muted tertiary
 *   line; the fork promotes it to a gold-underlined primary action.
 *   Two new image-badge slot groups (`footer.weidner_cares_link`,
 *   `footer.love_notes_link`) render the Weidner Cares and Love Notes
 *   brand badges hosted on the Multihub CDN as clickable icons under
 *   the CORT CTA — matching the via6 reference, where these are
 *   rectangular brand images rather than text pills. Defaults point at
 *   the corporate URLs (`weidner.com/weidnercares`,
 *   `weidner.com/lovenotes`) so the links work out of the box per
 *   property; editors can override image/URL/alt per slot.
 *
 * - ContactBlock: forked from the base oasis theme solely to default the
 *   brand logo slot to the property mono-black logo
 *   (`property_mono_black_logo`) instead of the primary logo. The card
 *   sits on a static light gray (#f3f4f6) that a mono-black mark reads
 *   cleanly against. All other structure and slots are unchanged from the
 *   base; editors can still pick any logo variant via the slot picker.
 *
 * - Hero: forked from the base oasis theme solely to soften the gradient
 *   overlay that darkens the background image for text contrast — its
 *   stops are lightened from the base (70/35/20 → 25/10/5) for a subtler
 *   wash. All other structure, tokens, and slots are unchanged.
 *
 * - HeroSolidBG: forked from the base oasis theme to let a page supply
 *   explicit default image URLs for the two stacked collage photos via
 *   `image{1,2}Url`/`image{1,2}Alt` props (in addition to the inherited
 *   collection/index bindings). The pet-policy page uses this to default
 *   the collage to the curated dog and cat photos from the canonical
 *   Weidner via6 pet-policy page. All other structure, tokens, and slots
 *   are unchanged from the base; pages that pass no image props keep the
 *   original `photo_gallery` collection defaults.
 *
 * - AccessibilityStatement: forked from the base oasis theme to hide the
 *   embedded AudioEye accessibility-statement iframe (the "audio eye"
 *   portal widget keyed off property_domain) on the Accessibility page.
 *   The editable heading, statement body, and contact link are unchanged.
 *
 * - GalleryStaggered: forked from the base oasis theme (together with its
 *   local `_galleryShared` helper) so the expanded lightbox keeps a
 *   consistent image size while paging between photos. The base sized the
 *   lightbox image-wrap to each photo's intrinsic aspect ratio, so flipping
 *   between portrait and landscape shots resized the framed area and shifted
 *   the prev/next arrows and counter. The fork pins the wrap to a fixed
 *   viewport-relative box and contains each image within it for a smooth,
 *   jump-free slideshow. The staggered grid and all slots are unchanged.
 *
 * - HomeAmenities: forked from the base oasis theme to make the three
 *   amenity image cards (Pools, Fitness Center, Concierge) clickable
 *   links to the Amenities page (`/amenities`) — the same destination as
 *   the CTA below. The base rendered the cards as static visual tiles;
 *   the fork wraps each card title in an `<a>` so the whole tile
 *   navigates. All tokens, slots, and styling are otherwise unchanged.
 *
 * - HomeNeighborhood / PropertyOverview: style-only forks of the base
 *   oasis sections. The base renders each section's primary CTA
 *   (`.hn-cta` / `.overview-cta`) as a secondary-filled pill that swaps to
 *   primary on hover. These forks adopt the Header's "Schedule a Tour"
 *   pill (`.header-nav-cta`) COLORS instead — primary fill at rest
 *   swapping to secondary on hover. Only the colors change; the section's
 *   own heading font and uppercase label are kept, as are all structure,
 *   tokens, slots, and the schema.
 *
 * - PetPolicy: forked from the base oasis theme to promote the CTA from the
 *   base's paired `petpolicy.cta_text` / `petpolicy.cta_url` tokens (rendered
 *   via a nested EditableToken-as-anchor) into a single `petpolicy.cta_link`
 *   link slot group with a real URL field (plus link text + target),
 *   defaulting to the Amenities page (`/amenities`). All other structure,
 *   tokens, slots, and styling are unchanged from the base.
 *
 * - WhiteBlock: forked from the base oasis theme solely to re-tune the
 *   spacer band's min-height to THIS fork's Header. The base WhiteBlock was
 *   sized for the base oasis nav (`h-24`/`h-32`), but the Weidner Header
 *   overrides the main nav bar to `h-24 lg:h-28` (96px/112px). The fork
 *   matches that exactly (96px mobile, ~164px lg+ = ~52px utility row +
 *   112px nav) so the hero sits flush beneath the navigation instead of
 *   below an over-tall white strip. The rich-text content token is
 *   unchanged.
 *
 * - TourBlock: style-only fork of the base oasis TourBlock on the Schedule
 *   A Tour page. Repaints the callout heading (`.tb-callout-heading`) from
 *   the secondary brand color to the on-primary text color (falling back to
 *   white) for stronger contrast on the primary-filled callout. The
 *   PropertyInfoPanel helpers are imported from the parent theme via the
 *   alias (not forked locally), mirroring the ContactBlock fork. All
 *   structure, tokens, slots, and the schema are otherwise unchanged.
 *
 * - Deals: new company-theme page section for the Weidner "Deals" /
 *   CORT Furniture Rental custom page. Ships the partner intro, CORT
 *   promo flyer, and CTA. The page is registered in pages.json with
 *   `visible: false` so properties opt in explicitly.
 *
 * - PricingVerbiage: adapted from the Weidner Preferred theme. Short
 *   pricing disclaimer bound to `custom_pricing_verbiage`, placed above
 *   ApartmentSync on the Floor Plans page.
 *
 * - AmenitiesList: forked from the base oasis theme to fix amenity columns
 *   getting clipped at the bottom. The base measured items-per-page before
 *   pagination controls mounted (so the last items were chopped once the
 *   controls stole height) and the stacked mobile layout could also clip
 *   via overflow:hidden. The fork reserves pagination height when measuring
 *   and sizes the list to content below the stacked breakpoint.
 */
const weidnerapartmenthomesoasisOverrides: Partial<ComponentRegistry> = {
  Footer,
  FutureResidentsClub,
  Header,
  ContactBlock,
  TourBlock,
  Hero,
  HeroSolidBG,
  AccessibilityStatement,
  GalleryStaggered,
  HomeAmenities,
  HomeNeighborhood,
  PropertyOverview,
  WhiteBlock,
  PetPolicy,
  Deals,
  PricingVerbiage,
  AmenitiesList,
};

/**
 * Final merged registry for Weidner Apartment Homes Oasis properties.
 * Inherits all components from the oasis theme, with Weidner Apartment Homes Oasis overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...weidnerapartmenthomesoasisOverrides,
};

export default registry;
